export type UserType = 'company' | 'contractor' | 'ambassador';

// Base user interface with common fields
export interface BaseUser {
  id: string;
  email: string;
  type: UserType;
  profilePhotoUrl?: string;
  backgroundPhotoUrl?: string;
  address: string;
  city: string;
  postalCode: string;
  country: string;
  phone: string;
  professionalEmail: string;
  website?: string;
  createdAt: Date;
  updatedAt: Date;
  emailVerified: boolean;
}

// Company specific fields
export interface Company extends BaseUser {
  type: 'company';
  companyName: string;
  businessType: string;
  siret: string;
  industry: string;
  specialization?: string;
  description: string;
  legalRepName: string;
  position: string;
  contactEmail: string;
  contactPhone: string;
  services?: string[];
  documents?: string[];
}

// Contractor specific fields
export interface Contractor extends BaseUser {
  type: 'contractor';
  firstName: string;
  lastName: string;
  serviceType: string;
  specialization?: string;
  legalStatus: string;
  siret?: string;
  experience: string;
  servicesDescription: string;
  availability: string;
  pricing: string;
  documents?: string[];
}

// Ambassador specific fields
export interface Ambassador extends BaseUser {
  type: 'ambassador';
  firstName: string;
  lastName: string;
  username: string;
  activityType: CustomField;
  ambassadorSpecialization: CustomField;
  ambassadorDescription: string;
  followersCount?: number;
  socialLinks: {
    instagram?: string;
    tiktok?: string;
    youtube?: string;
    [key: string]: string | undefined;
  };
  documents?: string[];
}

// Form data interface for registration
export interface RegistrationFormData {
  email: string;
  password: string;
  type: UserType;
  profilePhoto: File | null;
  backgroundPhoto: File | null;
  emailVerified: boolean;

  // Company & Contractor
  companyName?: string;
  businessType?: CustomField;
  siret?: string;
  industry?: CustomField;
  description?: string;

  // Address for all types
  address?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  phone?: string;
  professionalEmail?: string;
  website?: string;

  // Company Representative
  legalRepName?: string;
  position?: string;
  contactEmail?: string;
  contactPhone?: string;

  // Contractor specific
  firstName?: string;
  lastName?: string;
  serviceType?: CustomField;
  specialization?: string;
  legalStatus?: string;
  experience?: string;
  servicesDescription?: string;
  availability?: string;
  pricing?: string;

  // Ambassador specific
  username?: string;
  activityType?: CustomField;
  ambassadorSpecialization?: CustomField;
  ambassadorDescription?: string;
  followersCount?: number;
  socialLinks?: {
    instagram?: string;
    tiktok?: string;
    youtube?: string;
    [key: string]: string | undefined;
  };

  // Documents
  documents?: File[];
}

// Keys for business types based on language
export const businessTypeKeys = {
  fr: ['sarl', 'sas', 'auto', 'eurl', 'sa', 'sci', 'other'],
  en: ['ltd', 'plc', 'sole', 'partnership', 'llp', 'corp', 'llc', 'scorp', 'ccorp', 'nonprofit', 'other']
};

// Common keys for industries and service types (same list for both)
export const industryAndServiceKeys = [
  'admin',
  'agriculture',
  'arts',
  'audiovisual',
  'automotive',
  'banking',
  'building',
  'commerce',
  'law',
  'education',
  'energy',
  'hospitality',
  'industry',
  'it',
  'marketing',
  'defense',
  'fashion',
  'hr',
  'health',
  'science',
  'sports',
  'entertainment',
  'services',
  'space',
  'translation',
  'other'
];

// Helper type for custom field when "other" is selected
export interface CustomField {
  isOther: boolean;
  value: string;
  customValue: string | null;
}

export const activityTypeKeys = [
  'influencer',
  'commercial',
  'affiliate',
  'Business contributor',
  'other'
];

export const availabilityOptions = [
  'Temps plein',
  'Temps partiel',
  'Sur demande',
  'Week-end uniquement',
  'Flexible'
];
